import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TaskDefinitionVersionSpec, TaskSchemaSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {ProcessFlowchartTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import {
  InteractiveTemplateFlowchart,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
  TaskDefinitionVersionSpecEdge,
  TaskDefinitionVersionSpecFlowchart,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
} from './processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from './processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
} from './processFlowchartConfigEditorNodeTypes';

export function convertApplicationDataToSpec(
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL,
  executionType: ProcessExecutionType
): unknown {
  const applicationData = taskDefinitionVersion.applicationData;
  if (applicationData == null) {
    return null;
  }
  if (isInaccessible(applicationData)) {
    return null;
  }

  const adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> =
    PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
  const convertedApplicationData =
    adapter.convertTaskDefinitionVersionGQLToApplicationData(taskDefinitionVersion);

  return convertedApplicationData;
}

/**
 * Convert a task definition version GQL to a task definition version spec with task schema.
 * This spec will contain the frontend application data for the task definition version, and will
 * later be converted to the network spec application data for saving changes.
 *
 * @param taskDefinitionVersion - The task definition version GQL to convert.
 * @param allTaskSchemas - All task schemas to use for the conversion.
 * @returns The converted task definition version spec with task schema and frontend application data.
 */
export function convertTaskDefinitionVersionToSpec(
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL,
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>
): TaskDefinitionVersionSpecWithTaskSchema {
  let taskSchema: TaskSchemaSpec | null = null;

  const taskDefinitionSchema = taskDefinitionVersion.processTaskSchema;
  assert(!isInaccessible(taskDefinitionSchema), 'Task definition schema is unexpectedly inaccessible', {
    taskDefinitionVersion,
    taskDefinitionSchema,
  });
  const serverTaskSchema = allTaskSchemas.find((schema) => schema.id === taskDefinitionSchema.id);
  assert(serverTaskSchema != null, `Server task schema not found ${taskDefinitionSchema.id}`, {
    taskDefinitionSchema,
  });

  taskSchema = {
    id: taskDefinitionSchema.id,
    name: taskDefinitionSchema.name,
    executionType: taskDefinitionSchema.executionType,
    fields: serverTaskSchema.fields,
    outputSchema:
      serverTaskSchema.outputSchema != null
        ? {
            id: serverTaskSchema.outputSchema.id,
            name: serverTaskSchema.outputSchema.name,
            fields: serverTaskSchema.outputSchema.fields,
          }
        : null,
  };

  const applicationData = convertApplicationDataToSpec(
    taskDefinitionVersion,
    taskDefinitionSchema.executionType
  );

  return {
    clientId: taskDefinitionVersion.id,
    currentVersionId: taskDefinitionVersion.id,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskSchema.id,
      applicationData,
      taskSchema,
    },
  };
}

/**
 * Converts a frontend TaskDefinitionVersionSpecWithTaskSchema into a network spec TaskDefinitionVersionSpec.
 * This functionally removes the task schema from the spec, as it is not needed for updates.
 */
export function convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<unknown>,
  flowchartConfigId: string
): TaskDefinitionVersionSpec {
  const adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> =
    PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  const networkApplicationData = adapter.convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec,
    flowchartConfigId
  );

  return {
    clientId: taskDefinitionVersionSpec.clientId,
    currentVersionId: taskDefinitionVersionSpec.currentVersionId,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.id,
      applicationData: networkApplicationData,
    },
  };
}

/**
 * Converts the ReactFlow nodes and edges into frontend spec TaskDefinitionVersionSpecFlowchart,
 * so that application teams can just work in terms of task definition versions.
 */
export function convertInteractiveFlowchartToSpecFlowchart(
  flowchart: InteractiveTemplateFlowchart
): TaskDefinitionVersionSpecFlowchart {
  const nodes: Record<string, TaskDefinitionVersionSpec> = {};
  for (const node of flowchart.nodes) {
    if (isStaticTaskSchemaNodeData(node.data)) {
      nodes[node.id] = node.data.taskDefinitionVersionSpec;
    }
  }

  const edges: Array<TaskDefinitionVersionSpecEdge> = [];
  for (const edge of flowchart.edges) {
    const edgeSpec: TaskDefinitionVersionSpecEdge = {
      fromNodeClientId: edge.source,
      toNodeClientId: edge.target,
      type: edge.type,
    };

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      edgeSpec.fromFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId;
      edgeSpec.toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId;
    }

    edges.push(edgeSpec);
  }

  return {
    nodes,
    edges,
  };
}

export function convertStaticFlowchartToSpecFlowchart(
  flowchart: StaticTemplateFlowchart
): TaskDefinitionVersionSpecFlowchart {
  const nodes: Record<string, TaskDefinitionVersionSpec> = {};
  for (const node of flowchart.nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodes[node.id] = node.taskDefinitionVersionSpec;
    }
  }

  const edges: Array<TaskDefinitionVersionSpecEdge> = [];
  for (const edge of flowchart.edgesStaticData) {
    const edgeSpec: TaskDefinitionVersionSpecEdge = {
      fromNodeClientId: edge.source,
      toNodeClientId: edge.target,
      type: edge.type,
    };

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      edgeSpec.fromFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId;
      edgeSpec.toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId;
    }

    edges.push(edgeSpec);
  }

  return {
    nodes,
    edges,
  };
}

export function convertStaticFlowchartToSpecFlowchartWithTaskSchema(
  flowchart: StaticTemplateFlowchart
): TaskDefinitionVersionSpecWithTaskSchemaFlowchart {
  const nodes: Record<string, TaskDefinitionVersionSpecWithTaskSchema<unknown>> = {};
  for (const node of flowchart.nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodes[node.id] = node.taskDefinitionVersionSpec;
    }
  }

  const edges: Array<TaskDefinitionVersionSpecEdge> = [];
  for (const edge of flowchart.edgesStaticData) {
    const edgeSpec: TaskDefinitionVersionSpecEdge = {
      fromNodeClientId: edge.source,
      toNodeClientId: edge.target,
      type: edge.type,
    };

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      edgeSpec.fromFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId;
      edgeSpec.toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId;
    }

    edges.push(edgeSpec);
  }

  return {
    nodes,
    edges,
  };
}
